var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useUserLocalStorage } from "@/composables/userLocalStorage";
import { useHistoryStore } from "@/stores/historyStore";
import { addFavoriteToolQuery, getCurrentUser, removeFavoriteToolQuery, setCurrentThemeQuery, } from "@/stores/users/queries";
export const useUserStore = defineStore("userStore", () => {
    const currentUser = ref(null);
    const currentPreferences = ref(null);
    // explicitly pass current User, because userStore might not exist yet
    const toggledSideBar = useUserLocalStorage("user-store-toggled-side-bar", "tools", currentUser);
    const preferredListViewMode = useUserLocalStorage("user-store-preferred-list-view-mode", "grid", currentUser);
    let loadPromise = null;
    function $reset() {
        currentUser.value = null;
        currentPreferences.value = null;
        loadPromise = null;
    }
    const isAdmin = computed(() => {
        var _a, _b;
        return (_b = (_a = currentUser.value) === null || _a === void 0 ? void 0 : _a.is_admin) !== null && _b !== void 0 ? _b : false;
    });
    const isAnonymous = computed(() => {
        return !("email" in (currentUser.value || []));
    });
    const currentTheme = computed(() => {
        var _a, _b;
        return (_b = (_a = currentPreferences.value) === null || _a === void 0 ? void 0 : _a.theme) !== null && _b !== void 0 ? _b : null;
    });
    const currentFavorites = computed(() => {
        var _a;
        if ((_a = currentPreferences.value) === null || _a === void 0 ? void 0 : _a.favorites) {
            return currentPreferences.value.favorites;
        }
        else {
            return { tools: [] };
        }
    });
    function setCurrentUser(user) {
        currentUser.value = user;
    }
    function loadUser(includeHistories = true) {
        if (!loadPromise) {
            loadPromise = getCurrentUser()
                .then((user) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                currentUser.value = Object.assign(Object.assign({}, user), { isAnonymous: !user.email });
                currentPreferences.value = (_a = user === null || user === void 0 ? void 0 : user.preferences) !== null && _a !== void 0 ? _a : null;
                // TODO: This is a hack to get around the fact that the API returns a string
                if ((_b = currentPreferences.value) === null || _b === void 0 ? void 0 : _b.favorites) {
                    currentPreferences.value.favorites = JSON.parse((_d = (_c = user === null || user === void 0 ? void 0 : user.preferences) === null || _c === void 0 ? void 0 : _c.favorites) !== null && _d !== void 0 ? _d : { tools: [] });
                }
                if (includeHistories) {
                    const historyStore = useHistoryStore();
                    // load first few histories for user to start pagination
                    yield historyStore.loadHistories();
                }
            }))
                .catch((e) => {
                console.error("Failed to load user", e);
            })
                .finally(() => {
                loadPromise = null;
            });
        }
    }
    function setCurrentTheme(theme) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const currentTheme = yield setCurrentThemeQuery(currentUser.value.id, theme);
            if (currentPreferences.value) {
                currentPreferences.value.theme = currentTheme;
            }
        });
    }
    function addFavoriteTool(toolId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const tools = yield addFavoriteToolQuery(currentUser.value.id, toolId);
            setFavoriteTools(tools);
        });
    }
    function removeFavoriteTool(toolId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!currentUser.value || currentUser.value.isAnonymous) {
                return;
            }
            const tools = yield removeFavoriteToolQuery(currentUser.value.id, toolId);
            setFavoriteTools(tools);
        });
    }
    function setFavoriteTools(tools) {
        if (currentPreferences.value) {
            currentPreferences.value.favorites.tools = tools !== null && tools !== void 0 ? tools : { tools: [] };
        }
    }
    function setPreferredListViewMode(view) {
        preferredListViewMode.value = view;
    }
    function toggleSideBar(currentOpen = "") {
        toggledSideBar.value = toggledSideBar.value === currentOpen ? "" : currentOpen;
    }
    return {
        currentUser,
        currentPreferences,
        isAdmin,
        isAnonymous,
        currentTheme,
        currentFavorites,
        toggledSideBar,
        preferredListViewMode,
        loadUser,
        setCurrentUser,
        setCurrentTheme,
        setPreferredListViewMode,
        addFavoriteTool,
        removeFavoriteTool,
        toggleSideBar,
        $reset,
    };
});
